/**
 * SessionExitBanner — Beta investor-campaign UX
 *
 * Two variants of a non-blocking bottom banner:
 *   • "leave_early" — user clicked the in-site Exit button before completing
 *     all 4 actions (3 queries + 1 Demo). Dismissible. Redirects to the
 *     marketing site (REACT_APP_MARKETING_URL) on [Got it].
 *   • "completed"  — user has completed all 4 actions. Banner appears with a
 *     10-minute soft grace period during which they can browse Control Centre,
 *     Accuracy Controls, Revenue Share. The parent component starts a 10-min
 *     timer that auto-redirects when it elapses (regardless of [Got it] state).
 *
 * Copy is verbatim per user-provided spec. Placeholders ({{CONTACT_EMAIL}},
 * {{LINKEDIN_URL}}, {{FACEBOOK_URL}}, {{SOCIAL_TBD_URL}}, {{LANDING_URL}}) are
 * env-driven so marketing can hot-swap them without a redeploy. Each placeholder
 * falls back to a visible TBD tag so missing config is loud, not silent.
 */
import React from 'react';

const cfg = (key, fallback) => {
  const v = process.env[`REACT_APP_${key}`];
  return v && v.trim() ? v.trim() : fallback;
};

const LANDING_URL = cfg('MARKETING_URL', 'https://www.search.inc');
// SPD 2026-02-13 · operator directive (Exit_screen_v1.1.docx) — the
// investor-campaign copy now names kbeckles@search.inc as the contact
// address. Env override is still honoured; the fallback matches the
// operator-supplied verbatim string so a missing env doesn't leak a
// TBD placeholder to real users on the beta domain.
const CONTACT_EMAIL = cfg('CONTACT_EMAIL', 'kbeckles@search.inc');

const COPY = {
  // SPD 2026-02-13 (Exit_screen_v1.1.docx) — verbatim operator copy for
  // both banner variants. Each array entry is one paragraph. The final
  // paragraph ("If you'd like to ask us any questions...") is rendered
  // by <ContactBlock/> below so the email link stays clickable and any
  // future contact-address change is a single-line edit.
  leave_early: [
    "Hey there, We noticed you can still take some actions. Hopefully you\u2019re exiting early because you can\u2019t wait to invest with us. (that\u2019s a joke)",
    "If you haven\u2019t checked it out yet, The Accuracy Controls is a game changer (we feel) and so is the Revenue Share. Our self-learning memory system and your own personal wiki aka The Atlas is the coup de gras.",
  ],
  completed: [
    "You still have 10min in your session to peruse our site if you haven\u2019t already. The Accuracy Controls is a game changer (we feel) and so is the Revenue Share. Our self-learning memory system and your own personal wiki aka The Atlas is the coup de gras.",
    "We hope you have found our product a different experience. One you could see yourself using and hopefully investing in.",
  ],
};

// SPD 2026-02-13 (Exit_screen_v1.1.docx) — operator's v1.1 spec ended
// both variants with a single contact line and dropped the earlier
// LinkedIn / Facebook / TBD social block. ContactBlock now renders
// exactly that one line, with the email inside parentheses per the
// operator's own punctuation. The parenthesised form is intentional —
// do not "clean it up" without a fresh Copy-Judge approval.
const ContactBlock = () => (
  <p className="text-slate-700 text-lg leading-relaxed" data-testid="exit-banner-contact-line">
    If you&apos;d like to ask us any questions or would like to invest, please contact us at (&nbsp;
    <a
      href={`mailto:${CONTACT_EMAIL}`}
      className="text-cyan-700 font-medium underline-offset-2 hover:underline"
      data-testid="exit-banner-contact-email"
    >
      {CONTACT_EMAIL}
    </a>
    &nbsp;)
  </p>
);

export const SessionExitBanner = ({ variant, onDismiss, gracePctRemaining }) => {
  if (variant !== 'leave_early' && variant !== 'completed') return null;
  // SPD v5.75-fix (2026-07-11) — empty-copy guard. Operator cleared the
  // copy pending a fresh write. Do NOT render a bare banner (button only,
  // no message) while the copy is empty — it looks broken.
  if (!COPY[variant] || COPY[variant].length === 0) return null;

  const handleGotIt = () => {
    if (typeof onDismiss === 'function') onDismiss();
    // D9 (Feb 28, 2026, user direction): only the leave_early variant
    // redirects to the marketing site. The completed variant DISMISSES
    // the banner so the user can keep using the 10 min of remaining
    // session — the grace timer in App.js still auto-redirects them
    // on expiry.
    if (variant === 'leave_early' && typeof window !== 'undefined') {
      window.location.assign(LANDING_URL);
    }
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-gradient-to-t from-slate-100 to-slate-50 border-t-2 border-cyan-500 shadow-[0_-12px_32px_rgba(0,0,0,0.35)]"
      data-testid={`exit-banner-${variant}`}
      role="dialog"
      aria-label={variant === 'completed' ? 'Trial complete' : 'Leaving early'}
    >
      {/* Grace-period progress bar — completed variant only */}
      {variant === 'completed' && typeof gracePctRemaining === 'number' && (
        <div
          className="h-1 bg-cyan-500 transition-all duration-1000"
          style={{ width: `${Math.max(0, Math.min(100, gracePctRemaining))}%` }}
          data-testid="exit-banner-grace-bar"
        />
      )}
      <div className="max-w-5xl mx-auto px-6 py-5 sm:px-8 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 space-y-2.5">
            {COPY[variant].map((line, i) => (
              <p
                key={i}
                className={`text-slate-800 leading-relaxed ${i === 0 ? 'font-semibold text-base' : 'text-sm'}`}
                data-testid={`exit-banner-line-${i}`}
              >
                {line}
              </p>
            ))}
            <ContactBlock />
          </div>
          <button
            type="button"
            onClick={handleGotIt}
            className="self-start sm:self-end shrink-0 px-6 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg transition-colors shadow-md"
            data-testid="exit-banner-got-it-btn"
            aria-label="Acknowledge and go to home page"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionExitBanner;
